import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import CookiePreferencesModal from './CookiePreferencesModal'
import {
  initFacebookPixel,
  initGA4,
  initGoogleAds,
  initHotjar,
  setConsentDefaults,
  grantConsent,
  revokeConsent,
  setCookieConsent,
  getCookieConsent
} from '../../utils/analytics'

/**
 * Cookie Consent Banner Component
 * GDPR-compliant banner with equal prominence for accept/reject options
 * Matches the app's purple/pink gradient theme
 */
const CookieBanner = ({ onAcceptAll, onRejectAll, onSavePreferences }) => {
  const { t } = useTranslation('cookies')
  const [isVisible, setIsVisible] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [animateOut, setAnimateOut] = useState(false)

  useEffect(() => {
    const analyticsEnabled = import.meta.env.VITE_ENABLE_ANALYTICS === 'true'

    // Check if user has already made a cookie choice (cross-domain cookie)
    const preferences = getCookieConsent()

    if (preferences && analyticsEnabled) {
      // User has already consented - initialize analytics
      const initializeAnalytics = async () => {
        try {
          if (preferences.analytics || preferences.marketing || preferences.sessionRecording) {
            // Initialize analytics services ONLY after consent
            console.log('[Analytics] User has previous consent - initializing services')

            // Consent defaults are already set in index.html
            setConsentDefaults()

            // Initialize services based on specific consent
            if (preferences.analytics || preferences.marketing) {
              initFacebookPixel()
              await initGA4() // GA4 initialized AFTER consent (async)
              initGoogleAds()
            }

            // Hotjar requires explicit sessionRecording consent (GDPR)
            if (preferences.sessionRecording) {
              initHotjar()
            }

            // Enable dataLayer and grant consent
            grantConsent({
              analytics: preferences.analytics,
              marketing: preferences.marketing
            })
          } else {
            console.log('[Analytics] User previously rejected consent - no initialization')
          }
        } catch (error) {
          console.error('Error parsing cookie consent:', error)
          // Show banner on error
          setTimeout(() => setIsVisible(true), 500)
        }
      }

      initializeAnalytics()
    } else if (!preferences) {
      // No consent given yet - show banner
      // Do NOT initialize GA4 yet
      console.log('[Analytics] No consent yet - banner will be shown')
      setTimeout(() => setIsVisible(true), 500)
    }
  }, [])

  const handleAcceptAll = async () => {
    const prefs = {
      essential: true,
      analytics: true,
      sessionRecording: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }
    setCookieConsent(prefs)

    // Initialize analytics services NOW (after consent)
    const analyticsEnabled = import.meta.env.VITE_ENABLE_ANALYTICS === 'true'
    if (analyticsEnabled) {
      console.log('[Analytics] User accepted all - initializing services')
      setConsentDefaults()
      initFacebookPixel()
      await initGA4() // GA4 initialized AFTER user accepted (async)
      initGoogleAds()
      initHotjar() // Session recording explicitly consented
    }

    // Grant analytics consent
    grantConsent({
      analytics: true,
      marketing: true
    })

    onAcceptAll?.(prefs)
    handleClose()
  }

  const handleRejectAll = () => {
    const prefs = {
      essential: true, // Essential cookies are always required
      analytics: false,
      sessionRecording: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }
    setCookieConsent(prefs)

    // Revoke analytics consent
    revokeConsent()

    onRejectAll?.(prefs)
    handleClose()
  }

  const handleSavePreferences = async (preferences) => {
    setCookieConsent({
      ...preferences,
      timestamp: new Date().toISOString()
    })

    // Initialize analytics if any consent is given
    const analyticsEnabled = import.meta.env.VITE_ENABLE_ANALYTICS === 'true'
    if (analyticsEnabled && (preferences.analytics || preferences.marketing || preferences.sessionRecording)) {
      console.log('[Analytics] User gave consent - initializing services based on preferences')
      setConsentDefaults()

      // Initialize analytics/marketing services
      if (preferences.analytics || preferences.marketing) {
        initFacebookPixel()
        await initGA4() // GA4 initialized AFTER consent (async)
        initGoogleAds()
      }

      // Hotjar requires explicit sessionRecording consent (GDPR)
      if (preferences.sessionRecording) {
        initHotjar()
      }

      grantConsent({
        analytics: preferences.analytics,
        marketing: preferences.marketing
      })
    } else {
      revokeConsent()
    }

    onSavePreferences?.(preferences)
    setShowPreferences(false)
    handleClose()
  }

  const handleClose = () => {
    setAnimateOut(true)
    setTimeout(() => {
      setIsVisible(false)
      setAnimateOut(false)
    }, 300)
  }

  if (!isVisible) return null

  return (
    <>
      {/* Cookie Banner */}
      <div
        className={`
          fixed bottom-0 left-0 right-0 z-40
          bg-white/95 backdrop-blur-md
          border-t border-gray-200
          shadow-2xl shadow-black/10
          transition-all duration-300 ease-out
          ${animateOut ? 'translate-y-full opacity-0' : 'translate-y-0 opacity-100'}
        `}
        role="region"
        aria-label={t('banner.ariaLabel')}
        aria-live="polite"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Text Content */}
            <div className="flex-1 space-y-2">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('banner.title', '🍪 We value your privacy')}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed max-w-3xl">
                {t('banner.description',
                  'We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. By clicking "Accept All", you consent to our use of cookies.'
                )}
              </p>
            </div>

            {/* Button Group */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              {/* Reject All - Equal prominence as per GDPR */}
              <button
                onClick={handleRejectAll}
                className="
                  px-6 py-2.5 rounded-lg font-semibold text-sm
                  bg-gray-100 text-gray-700
                  border border-gray-300
                  hover:bg-gray-200 hover:border-gray-400
                  focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2
                  transition-all duration-200
                  min-w-[120px]
                "
                aria-label={t('banner.rejectAll.ariaLabel')}
              >
                {t('banner.rejectAll', 'Reject All')}
              </button>

              {/* Customize */}
              <button
                onClick={() => setShowPreferences(true)}
                className="
                  px-6 py-2.5 rounded-lg font-semibold text-sm
                  bg-white text-purple-600
                  border-2 border-purple-600
                  hover:bg-purple-50 hover:border-purple-700
                  focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
                  transition-all duration-200
                  min-w-[120px]
                "
                aria-label={t('banner.customize.ariaLabel')}
              >
                {t('banner.customize', 'Customize')}
              </button>

              {/* Accept All - Using gradient theme */}
              <button
                onClick={handleAcceptAll}
                className="
                  px-6 py-2.5 rounded-lg font-semibold text-sm
                  bg-gradient-to-r from-purple-600 to-pink-500
                  text-white
                  hover:from-purple-700 hover:to-pink-600
                  focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2
                  transition-all duration-200
                  shadow-lg shadow-purple-500/25
                  min-w-[120px]
                  transform hover:scale-105 active:scale-95
                "
                aria-label={t('banner.acceptAll.ariaLabel')}
              >
                {t('banner.acceptAll', 'Accept All')}
              </button>
            </div>
          </div>

          {/* Privacy Policy Link */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              {t('banner.learnMore', 'Learn more in our')}{' '}
              <a
                href="/privacy-policy"
                className="text-purple-600 hover:text-purple-700 underline font-medium"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('banner.privacyPolicy', 'Privacy Policy')}
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Cookie Preferences Modal */}
      <CookiePreferencesModal
        isOpen={showPreferences}
        onClose={() => setShowPreferences(false)}
        onSave={handleSavePreferences}
      />
    </>
  )
}

export default CookieBanner